// @ts-check
import { useAssignmentDataGridContext } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.context';
import ConfirmModal from '~/components/Shared/ConfirmModal';
import ReAssignModal from '~/components/AdminPages/ManageSchedules/ReAssignModal';
import { delete_assignment, patchAssignment, get_schedule } from '~/api/zero-api';
import NotificationAlert from '~/other/NotificationAlert';
import { EventNames } from '~/components/Forms/AssignmentDataGrid/AssignmentDataGrid.utils';
import { useZeroContext } from '~/components/ZeroContext';

export function AssignmentDataGridModals() {
    const { activeModal, setActiveModal } = useAssignmentDataGridContext();
    const { events } = useZeroContext();

    const closeModal = () => {
        setActiveModal(null);
    };

    const refreshGrid = () => {
        events.emit(EventNames.REFRESH_DATA);
    };

    const handleExcuseAssignment = async () => {
        if (!activeModal?.assignment) return;

        try {
            const body = JSON.stringify({ excused: true });
            await patchAssignment(activeModal.assignment.assignment_uuid, body);
            NotificationAlert("success", "", "Assignment excused successfully.");
            refreshGrid();
        } catch (error) {
            NotificationAlert("error", "", "Could not excuse assignment.");
        }
        
        closeModal();
    };

    const handleRequireAssignment = async () => {
        if (!activeModal?.assignment) return;

        try {
            const body = JSON.stringify({ excused: false });
            await patchAssignment(activeModal.assignment.assignment_uuid, body);
            NotificationAlert("success", "", "Assignment is now required.");
            refreshGrid();
        } catch (error) {
            NotificationAlert("error", "", "Could not require assignment.");
        }
        
        closeModal();
    };

    const handleDeleteAssignment = async () => {
        if (!activeModal?.assignment) return;

        try {
            await delete_assignment(activeModal.assignment.assignment_uuid);
            NotificationAlert("success", "", "Assignment deleted successfully.");
            refreshGrid();
        } catch (error) {
            NotificationAlert("error", "", "Could not delete assignment.");
        }
        
        closeModal();
    };

    const handleReassignClick = async () => {
        if (!activeModal?.assignment) return;

        try {
            const response = await get_schedule(activeModal.assignment.scheduler_uuid);
            const data = await response.json();
            
            setActiveModal({
                ...activeModal,
                key: 'reassign_modal',
                scheduler: data.scheduler
            });
        } catch (error) {
            NotificationAlert("error", "", "Could not load schedule data for reassignment.");
            closeModal();
        }
    };

    if (!activeModal) {
        return null;
    }

    const { key, assignment } = activeModal;

    switch (key) {
        case 'excuse_assignment':
            return (
                <ConfirmModal
                    show={true}
                    cancel={closeModal}
                    title="Excuse Assignment"
                    body="Are you sure you want to excuse this assignment?"
                    confirmButtonName="Excuse Assignment"
                    confirm={handleExcuseAssignment}
                />
            );

        case 'require_assignment':
            return (
                <ConfirmModal
                    show={true}
                    cancel={closeModal}
                    title="Require Assignment"
                    body="Are you sure you want to require this assignment?"
                    confirmButtonName="Require Assignment"
                    confirm={handleRequireAssignment}
                />
            );

        case 'delete_assignment':
            return (
                <ConfirmModal
                    show={true}
                    cancel={closeModal}
                    title="Delete Assignment"
                    body="Are you sure you want to delete this assignment?"
                    confirmButtonName="Delete Assignment"
                    confirm={handleDeleteAssignment}
                />
            );

        case 'reassign_assignment':
            handleReassignClick();
            return null;

        case 'reassign_modal':
            return (
                <ReAssignModal
                    show={true}
                    cancel={closeModal}
                    assignmentId={assignment.assignment_uuid}
                    reAssignUser={assignment.assigned_to}
                    responders={activeModal.scheduler?.subscribers || []}
                    schedule={activeModal.scheduler}
                    refresh={() => {
                        refreshGrid();
                        closeModal();
                    }}
                />
            );

        default:
            return null;
    }
} 